import {useEffect, useCallback, useRef} from 'react'
import {useBlocker} from 'react-router-dom'
import {store} from '../../common/store'
import type {DeviceConfig} from '../../common/components/types'
import {serializeConfig} from '../components/topMenuBar/serializeConfig'
import {getSavedSnapshot, subscribeSavedSnapshot} from '../../common/components/store/savedSnapshotStore'
import {stableStringify} from '../../common/utils/stableStringify'

/** Check whether the current editor state differs from the last saved snapshot */
const computeIsDirty = (deviceConfig: DeviceConfig): boolean => {
  const snapshot = getSavedSnapshot()
  if (!snapshot) return false
  const current = stableStringify(serializeConfig(store.getState().present, deviceConfig))
  return current !== snapshot
}

/**
 * Leave guard for the editor.
 * Blocks in-app navigation via useBlocker and tab/browser close via beforeunload.
 */
const useLeaveGuard = (deviceConfig: DeviceConfig) => {
  const deviceConfigRef = useRef(deviceConfig)
  useEffect(() => {
    deviceConfigRef.current = deviceConfig
  }, [deviceConfig])

  const shouldBlock = useCallback(
    ({currentLocation, nextLocation}: {currentLocation: {pathname: string}; nextLocation: {pathname: string}}) => {
      if (currentLocation.pathname === nextLocation.pathname) return false
      return computeIsDirty(deviceConfigRef.current)
    },
    [],
  )

  const blocker = useBlocker(shouldBlock)

  // beforeunload — browser native dialog for tab close / external navigation.
  // Re-evaluates on BOTH Redux state changes AND saved-snapshot writes so a
  // successful save clears `onbeforeunload` immediately (a save doesn't dispatch
  // any Redux action after writing the snapshot, so without the snapshot
  // subscription the handler would stay attached until the next state change).
  useEffect(() => {
    const recompute = () => {
      const dirty = computeIsDirty(deviceConfigRef.current)
      if (dirty) {
        window.onbeforeunload = (e: BeforeUnloadEvent) => {
          e.preventDefault()
        }
      } else {
        window.onbeforeunload = null
      }
    }
    const unsubscribeStore = store.subscribe(recompute)
    const unsubscribeSnapshot = subscribeSavedSnapshot(recompute)

    return () => {
      unsubscribeStore()
      unsubscribeSnapshot()
      window.onbeforeunload = null
    }
  }, [])

  return {blocker}
}

export default useLeaveGuard
